import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Pencil, Trash2 } from "lucide-react";
import {
  getModelPricing,
  createModelPricing,
  updateModelPricing,
  deleteModelPricing,
  getProviders,
  type AIModelPricing as AIModelPricingType,
} from "@/services/admin-api";
import {
  createPricingSchema,
  updatePricingSchema,
  type CreatePricingInput,
  type UpdatePricingInput,
} from "@/lib/schemas/ai-schemas";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

export default function AIModelPricing() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AIModelPricingType | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AIModelPricingType | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["ai-pricing"],
    queryFn: getModelPricing,
  });

  const { data: providersData } = useQuery({
    queryKey: ["ai-providers"],
    queryFn: getProviders,
  });

  const pricing = data?.data ?? [];
  const providers = providersData?.data ?? [];

  // Group by provider
  const pricingByProvider = pricing.reduce(
    (acc, item) => {
      if (!acc[item.providerId]) acc[item.providerId] = [];
      acc[item.providerId].push(item);
      return acc;
    },
    {} as Record<string, AIModelPricingType[]>,
  );

  // --- Create ---
  const createForm = useForm<z.input<typeof createPricingSchema>, unknown, CreatePricingInput>({
    resolver: zodResolver(createPricingSchema),
    defaultValues: { providerId: "", model: "", inputPricePerMToken: 0, outputPricePerMToken: 0 },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreatePricingInput) => {
      const { providerId, ...rest } = data;
      return createModelPricing(providerId, rest);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-pricing"] });
      toast({ title: "Pricing created" });
      setCreateOpen(false);
      createForm.reset();
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create pricing",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // --- Edit ---
  const editForm = useForm<z.input<typeof updatePricingSchema>, unknown, UpdatePricingInput>({
    resolver: zodResolver(updatePricingSchema),
  });

  const editMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePricingInput }) =>
      updateModelPricing(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-pricing"] });
      toast({ title: "Pricing updated" });
      setEditTarget(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update pricing",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // --- Delete ---
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteModelPricing(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-pricing"] });
      toast({ title: "Pricing deleted" });
      setDeleteTarget(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete pricing",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const openEdit = (item: AIModelPricingType) => {
    setEditTarget(item);
    editForm.reset({
      inputPricePerMToken: item.inputPricePerMToken,
      outputPricePerMToken: item.outputPricePerMToken,
      cachedInputPricePerMToken: item.cachedInputPricePerMToken ?? undefined,
    });
  };

  if (isLoading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="AI Model Pricing"
        description="Manage model pricing (per 1M tokens)"
        actions={
          <Button
            onClick={() => {
              setCreateOpen(true);
              createForm.reset();
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Pricing
          </Button>
        }
      />

      <div className="space-y-6">
        {Object.entries(pricingByProvider).map(([providerId, models]) => (
          <div key={providerId} className="space-y-2">
            <div className="px-1">
              <Badge variant="secondary" className="text-base capitalize">
                {providerId}
              </Badge>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Model</TableHead>
                  <TableHead className="text-right">Input ($/1M)</TableHead>
                  <TableHead className="text-right">Output ($/1M)</TableHead>
                  <TableHead className="text-right">Cached ($/1M)</TableHead>
                  <TableHead className="text-right">Last Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {models.map((model) => (
                  <TableRow key={model.id}>
                    <TableCell className="whitespace-nowrap font-mono text-sm">
                      {model.model}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right text-sm">
                      ${model.inputPricePerMToken.toFixed(3)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right text-sm">
                      ${model.outputPricePerMToken.toFixed(3)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right text-sm text-muted-foreground">
                      {model.cachedInputPricePerMToken != null
                        ? `$${model.cachedInputPricePerMToken.toFixed(3)}`
                        : "\u2014"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right text-sm text-muted-foreground">
                      {new Date(model.updatedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right text-sm space-x-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(model)}
                        aria-label={`Edit ${model.model}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteTarget(model)}
                        aria-label={`Delete ${model.model}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Model Pricing</DialogTitle>
            <DialogDescription>Add pricing for a new model.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={createForm.handleSubmit((data) => createMutation.mutate(data))}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label>Provider</Label>
              <Controller
                control={createForm.control}
                name="providerId"
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {createForm.formState.errors.providerId && (
                <p className="text-sm text-red-600">
                  {createForm.formState.errors.providerId.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-model">Model</Label>
              <Input
                id="create-model"
                placeholder="gpt-4o-mini"
                {...createForm.register("model")}
              />
              {createForm.formState.errors.model && (
                <p className="text-sm text-red-600">{createForm.formState.errors.model.message}</p>
              )}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-input-price">Input $/1M</Label>
                <Input
                  id="create-input-price"
                  type="number"
                  step="0.001"
                  {...createForm.register("inputPricePerMToken")}
                />
                {createForm.formState.errors.inputPricePerMToken && (
                  <p className="text-sm text-red-600">
                    {createForm.formState.errors.inputPricePerMToken.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-output-price">Output $/1M</Label>
                <Input
                  id="create-output-price"
                  type="number"
                  step="0.001"
                  {...createForm.register("outputPricePerMToken")}
                />
                {createForm.formState.errors.outputPricePerMToken && (
                  <p className="text-sm text-red-600">
                    {createForm.formState.errors.outputPricePerMToken.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-cached-price">Cached $/1M</Label>
                <Input
                  id="create-cached-price"
                  type="number"
                  step="0.001"
                  {...createForm.register("cachedInputPricePerMToken")}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? "Creating..." : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editTarget !== null} onOpenChange={() => setEditTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Pricing</DialogTitle>
            <DialogDescription>
              Update pricing for{" "}
              <strong>
                {editTarget?.providerId} / {editTarget?.model}
              </strong>
              .
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={editForm.handleSubmit((data) =>
              editMutation.mutate({ id: editTarget!.id, data }),
            )}
            className="space-y-4"
          >
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-input-price">Input $/1M</Label>
                <Input
                  id="edit-input-price"
                  type="number"
                  step="0.001"
                  {...editForm.register("inputPricePerMToken")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-output-price">Output $/1M</Label>
                <Input
                  id="edit-output-price"
                  type="number"
                  step="0.001"
                  {...editForm.register("outputPricePerMToken")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-cached-price">Cached $/1M</Label>
                <Input
                  id="edit-cached-price"
                  type="number"
                  step="0.001"
                  {...editForm.register("cachedInputPricePerMToken")}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditTarget(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={editMutation.isPending}>
                {editMutation.isPending ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={() => setDeleteTarget(null)}
        title="Delete Pricing"
        description={`Are you sure you want to delete pricing for "${deleteTarget?.providerId} / ${deleteTarget?.model}"?`}
        onConfirm={() => deleteMutation.mutate(deleteTarget!.id)}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
